'use client'

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { cn } from '@nt/ui/cn'
import { dialogRecipe } from '@nt/ui/recipes'
import { XIcon } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { ModalPresence } from '@/hooks/useModalOpen'

function Dialog({ dismissible, ...props }: DialogPrimitive.Root.Props & { dismissible?: boolean }) {
  return (
    <DialogPrimitive.Root
      data-slot="dialog"
      disablePointerDismissal={dismissible === false ? true : undefined}
      {...props}
    />
  )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop data-slot="dialog-overlay" className={cn(dialogRecipe.overlay, className)} {...props} />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(dialogRecipe.content, 'flex flex-col gap-6 [scrollbar-gutter:stable_both-edges]', className)}
        {...props}
      >
        <ModalPresence />
        {showCloseButton && (
          // Zero-height sticky rail, pulled out to the popup's own edges, that
          // anchors the close button. A dialog that opts into `overflow-y-auto`
          // makes THIS element the scroll container, and an absolutely
          // positioned child of a scroll container scrolls away with the
          // content — the close button used to disappear as soon as a tall
          // dialog was scrolled. Sticky keeps it pinned, and the rail must be
          // the first child of a FLEX column: a grid item's sticky movement is
          // confined to its own grid row, which is far too short to hold.
          <div className="pointer-events-none sticky -top-6 z-10 -mx-6 -mt-6 h-0 self-stretch">
            <DialogPrimitive.Close
              data-slot="dialog-close"
              render={<Button variant="ghost" className="pointer-events-auto absolute right-4 top-4" size="icon-sm" />}
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>
        )}
        {children}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

/**
 * An opaque title row that stays pinned under the close rail of a scrolling
 * `DialogContent`. Put it first in the content: it reuses the rail's `-top-6`
 * sticky offset and pulls out to the popup's edges, and `pr-16` keeps the title
 * clear of the close button, so fields scroll UNDER the row instead of under
 * the button (at 390px the button used to sit on a form field).
 */
function DialogStickyHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-sticky-header"
      className={cn('sticky -top-6 z-5 -mx-6 -mt-6 bg-card pt-6 pr-16 pb-2 pl-6', className)}
      {...props}
    />
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-header" className={cn(dialogRecipe.header, className)} {...props} />
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div data-slot="dialog-footer" className={cn(dialogRecipe.footer, className)} {...props}>
      {children}
      {showCloseButton && <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return <DialogPrimitive.Title data-slot="dialog-title" className={cn(dialogRecipe.title, className)} {...props} />
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        dialogRecipe.description,
        'hover:[a]:*:text-foreground [a]:*:underline [a]:*:underline-offset-3',
        className,
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogStickyHeader,
  DialogTitle,
  DialogTrigger,
}
